import { format } from 'date-fns';
import { useMemo, useRef, useState } from 'react';
import { type LayoutChangeEvent, ScrollView, View } from 'react-native';
import { Badge, Box, HStack, Text } from '../../../components/primitives';
import { colors } from '../../../theme/colors';
import type { CalendarEvent } from '../../../types/calendar';
import { useScrollToNow } from '../hooks/useScrollToNow';
import {
  getViewerTimeZone,
  getWeekRange,
  isToday,
  type LocalDate,
  localDateKey,
  toNativeDate,
} from '../utils/calendarDate';
import { DEFAULT_DAY_WINDOW, getNowLineRatio, type TimeWindow } from '../utils/calendarDayGeometry';
import { eventTouchesLocalDay } from '../utils/calendarEventBucketing';
import {
  buildHourRows,
  formatHourLabel,
  GUTTER_WIDTH,
  getGridHeight,
  HOUR_HEIGHT,
} from '../utils/calendarHourGrid';
import { CalendarDayEventBlock } from './CalendarDayEventBlock';
import { CalendarWeekDayColumn } from './CalendarWeekDayColumn';

interface CalendarWeekGridProps {
  anchor: LocalDate;
  events: CalendarEvent[];
  timeZone?: string;
  weekStartsOn?: 0 | 1;
  /** Injectable for tests; defaults to the real current instant. */
  now?: Date;
  /** The slice of the day every column renders. One gutter serves all seven,
   * so this is deliberately grid-wide rather than per-column. */
  window?: TimeWindow;
  /** Opens the edit form — the drill-down destination from the month grid. */
  onEventPress?: (event: CalendarEvent) => void;
  /** Desktop drag-to-reschedule; see useEventDrag for why mobile has none. */
  onReschedule?: (event: CalendarEvent, span: { startsAt: string; endsAt: string }) => void;
}

interface DayBucket {
  date: LocalDate;
  timedEvents: CalendarEvent[];
  allDayEvents: CalendarEvent[];
}

// Web is where this view earns its keep. Columns are simple equal-width
// flex:1 cells rather than fixed-width + a synced horizontal scroll: a
// second scroll surface that has to stay pixel-locked to the header (and to
// the hourly grid's own vertical ScrollView) is exactly the kind of
// contortion the brief said not to build for phones — mobile already
// defaults to Day. On a narrow viewport this gets cramped, not broken; the
// switcher is right there if that's not the useful view for the moment.
export function CalendarWeekGrid({
  anchor,
  events,
  timeZone = getViewerTimeZone(),
  weekStartsOn = 0,
  now = new Date(),
  window = DEFAULT_DAY_WINDOW,
  onEventPress,
  onReschedule,
}: Readonly<CalendarWeekGridProps>) {
  const scrollRef = useRef<ScrollView>(null);
  const [viewportHeight, setViewportHeight] = useState(0);
  const hourRows = buildHourRows(window);
  // `anchor` is a stable reference from the route's useState (only replaced
  // by an explicit setAnchor call), so depending on it directly is exact —
  // no over-narrow re-render risk that field-by-field deps would guard
  // against, and it matches what the closure actually captures.
  const days = useMemo(() => getWeekRange(anchor, weekStartsOn), [anchor, weekStartsOn]);

  // The week has at most one "today" column; that is the one worth opening on.
  const nowRatio = days.reduce<number | null>(
    (found, date) => found ?? getNowLineRatio(date, timeZone, now, window),
    null,
  );

  useScrollToNow({ scrollRef, nowRatio, gridHeight: getGridHeight(window), viewportHeight });

  const buckets = useMemo<DayBucket[]>(() => {
    return days.map((date) => {
      const dateKey = localDateKey(date);
      const touching = events.filter((event) => eventTouchesLocalDay(event, dateKey, timeZone));
      return {
        date,
        timedEvents: touching.filter((event) => !event.allDay),
        allDayEvents: touching.filter((event) => event.allDay),
      };
    });
  }, [days, events, timeZone]);

  const hasAllDayEvents = buckets.some((bucket) => bucket.allDayEvents.length > 0);

  return (
    <Box testID="calendar_week_grid" flex={1}>
      <HStack style={{ paddingLeft: GUTTER_WIDTH }}>
        {buckets.map(({ date }) => {
          const today = isToday(date, timeZone, now);
          const dateLabel = format(toNativeDate(date), 'EEEE, MMMM d, yyyy');
          return (
            <Box
              key={localDateKey(date)}
              testID={`calendar_week_header_${date.year}-${date.month}-${date.day}`}
              flex={1}
              alignItems="center"
              padding="xs"
              accessibilityLabel={`${today ? 'Today, ' : ''}${dateLabel}`}
            >
              <Text variant="caption" color={colors.text.tertiary}>
                {format(toNativeDate(date), 'EEE')}
              </Text>
              {/* "Today" is never colour alone: a bordered pill (shape +
                  border) here, matching CalendarMonthDayCell's Badge, plus
                  the "Today" prefix on accessibilityLabel above. */}
              {today ? (
                <Badge variant="outline" color="cyan" size="sm">
                  {String(date.day)}
                </Badge>
              ) : (
                <Text variant="label" color={colors.text.primary} style={{ fontWeight: '600' }}>
                  {date.day}
                </Text>
              )}
            </Box>
          );
        })}
      </HStack>

      {hasAllDayEvents && (
        <HStack
          testID="calendar_week_all_day_strip"
          style={{
            paddingLeft: GUTTER_WIDTH,
            borderBottomWidth: 1,
            borderColor: colors.border.muted,
          }}
        >
          {buckets.map(({ date, allDayEvents }) => (
            <Box key={localDateKey(date)} flex={1} padding="xxs">
              {allDayEvents.map((event) => (
                <CalendarDayEventBlock
                  key={event.id}
                  event={event}
                  onPress={onEventPress}
                  testIDPrefix="calendar_week_all_day_event"
                />
              ))}
            </Box>
          ))}
        </HStack>
      )}

      <ScrollView
        ref={scrollRef}
        showsVerticalScrollIndicator={false}
        style={{ flex: 1 }}
        onLayout={(event: LayoutChangeEvent) => setViewportHeight(event.nativeEvent.layout.height)}
      >
        <View style={{ flexDirection: 'row' }}>
          <View style={{ width: GUTTER_WIDTH }}>
            {hourRows.map((hour) => (
              <Box
                key={hour}
                style={{ height: HOUR_HEIGHT }}
                paddingRight="xs"
                alignItems="flex-end"
              >
                <Text variant="caption" color={colors.text.tertiary}>
                  {formatHourLabel(hour)}
                </Text>
              </Box>
            ))}
          </View>

          {buckets.map(({ date, timedEvents }) => (
            <CalendarWeekDayColumn
              key={localDateKey(date)}
              date={date}
              events={timedEvents}
              timeZone={timeZone}
              now={now}
              window={window}
              onEventPress={onEventPress}
              onReschedule={onReschedule}
            />
          ))}
        </View>
      </ScrollView>
    </Box>
  );
}
